import React, { useState } from "react";
import { Link } from "react-router-dom";
import { UserAdd02Icon } from "../../assets/icons";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../CommonElements/PageTitle";
import { Stack } from "react-bootstrap";
import { UserCard } from "./UserCard";
import { Container } from "reactstrap";
import { useGetUsersQuery } from "../../services/user";
import { useDebouncedSearch } from "../../utils/hooks/useDebouncedSearch";
import { useScreenSize } from "../../utils/hooks/useScreenSize ";
import { Can } from "../../CommonElements/PermissionGate";
import BtnAsLink from "../../Component/BtnAsLink";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
import { USER_MANAGEMENT_PERMISSIONS_FULL_NAMES } from "@/utils/constants";
import { SearchFilter } from "@/CommonElements/SearchFilter/SearchFilter";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import { UsersListGroupFilterSelect } from "./UsersListGroupFilterSelect";


const Users = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const [currentPage, setCurrentPage] = useState(1);
  const [{ count, ...filters }, setFilters] = useState({
    sector_id: undefined,
    department_id: undefined,
    job_destination_id: undefined,
    type: undefined,
    nationality_code: "",
    group_id: undefined,
  });

  // Use debounced search hook
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const { width } = useScreenSize();

  const filterFields = [
    {
      label: t("SECTOR"),
      name: "sector_id",
      type: "select",
      optionKey: "job_sectors",
      options: (sector) => ({
        value: sector.id,
        label: [sector.id, getLocalizedText(sector.name_ar, sector.name_en)].join(" - "),
      })
    },
    {
      label: t("DEPARTMENT"),
      name: "department_id",
      type: "select",
      optionKey: "departments",
      options: (department) => ({
        value: department.id,
        label: [department.id, getLocalizedText(department.name_ar, department.name_en)].join(" - "),
      })
    },
    {
      label: t("EMPLOYER"),
      name: "job_destination_id",
      type: "select",
      optionKey: "job_destinations",
      options: (jobDestination) => ({
        value: jobDestination.id,
        label: [jobDestination.id, getLocalizedText(jobDestination.name_ar, jobDestination.name_en)].join(" - "),
      })
    },
    {
      label: t("TYPE"),
      name: "type",
      type: "select",
      optionKey: "employee_types",
      options: (type) => ({
        value: type.name,
        label: [getLocalizedText(type.display_name_ar, type.display_name_en)].join(" - "),
      })
    },
    {
      label: t("NATIONALITY"),
      name: "nationality_code",
      type: "select",
      optionKey: "nationalities",
      options: (nationality) => ({
        value: nationality.id,
        label: <SelectCountry.Display value={nationality.id} />,
      }),
    },
    {
      label: t("GROUP"),
      name: "group_id",
      type: "infiniteSelect",
      Component: (props) => <UsersListGroupFilterSelect {...props} />,
      placeholder: t("GROUP"),
    },
  ];
  // Pass search parameter and filters to the query
  const {
    data: usersRes,
    isLoading,
    isFetching,
  } = useGetUsersQuery({
    search: debouncedValue,
    per_page: 5,
    page: currentPage,
    ...filters,
  });

  const users = usersRes?.data?.data || [];
  const loading = isLoading || isFetching;

  // Create an array of 3 items for skeleton/loading demonstration
  const skeletonItems = Array.from({ length: 3 }, (_, index) => ({
    id: index,
  }));

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDebouncedChange = (searchValue) => {
    setFilters(searchValue);
    setCurrentPage(1);
  };

  return (
    <div id="drawer_container">
      <PageHeader sticky={false} title={t("SYSTEM_USERS")}>
        <Can permission={USER_MANAGEMENT_PERMISSIONS_FULL_NAMES.create_user_for_user_management}>
          <div className="d-flex align-items-center gap-2">
            <BtnAsLink
              className="btn-primary"
              link="create"
              icon={<UserAdd02Icon />}
            >
              {t("ADD_NEW_USER")}
            </BtnAsLink>
          </div>
        </Can>
      </PageHeader>
      <Container fluid>

        <Stack gap={3} className="mt-4">
          <Container
            className="sticky-top z-1 w-100 p-3 bg-white"
            fluid
            style={{ top: width <= 575 ? "71px" : "78px" }}
          >

            <SearchFilter
              filterFields={filterFields}
              onChange={handleDebouncedChange}
              debounced
            />
            {/* Desktop: Dropdown, Mobile: Drawer
              {width > 768 ? (
                <Dropdown
                  open={filtersOpen}
                  onOpenChange={setFiltersOpen}
                  placement="bottomRight"
                  trigger={["click"]}
                  dropdownRender={() => (
                    <div style={contentStyle}>
                      <FilterContent />
                      <Stack direction="horizontal" gap={2}>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleApplyFilters}
                          className="flex-fill"
                        >
                          <Space>
                            <HugeiconsIcon
                              className="d-flex"
                              align="center"
                              size={20}
                              icon={FilterIcon}
                            />
                            {t("APPLY_FILTERS")}
                          </Space>
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleClearFilters}
                          className="flex-fill btn-outline"
                        >
                          {t("CLEAR_FILTERS")}
                        </Button>
                      </Stack>
                    </div>
                  )}
                >
                  <Button
                    variant="secondary"
                    size="xs"
                    className="outline btn-icon"
                  >
                    <HugeiconsIcon icon={FilterIcon} />
                  </Button>
                </Dropdown>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    size="xs"
                    className="outline btn-icon"
                    onClick={() => setFiltersOpen(true)}
                  >
                    <HugeiconsIcon icon={FilterIcon} />
                  </Button>

                  <Drawer
                    title={t("FILTER_USERS")}
                    placement="bottom"
                    height="80%"
                    open={filtersOpen}
                    onClose={() => setFiltersOpen(false)}
                    className="filter-drawer"
                    destroyOnClose={false}
                    getContainer={() =>
                      document.getElementById("drawer_container")
                    }
                  >
                    <FilterContent />
                    <div className="d-flex gap-2 mt-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleClearFilters}
                        className="flex-fill btn-outline"
                      >
                        {t("CLEAR_FILTERS")}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleApplyFilters}
                        className="flex-fill"
                      >
                        {t("APPLY_FILTERS")}
                      </Button>
                    </div>
                  </Drawer>
                </>
              )} */}
          </Container>
          <Stack>
            {loading ? (
              skeletonItems.map((item) => (
                <UserCard key={item?.id} loading={loading} />
              ))
            ) : users.length === 0 ? (
              <div className="users-empty-state text-center">
                <div className="users-empty-state-icon">
                  <UserAdd02Icon
                    style={{ width: "64px", height: "64px", opacity: "0.3" }}
                  />
                </div>
                <h4 className="users-empty-state-title">
                  {debouncedValue
                    ? t("NO_SEARCH_RESULTS")
                    : t("NO_USERS_FOUND")}
                </h4>
                <p className="users-empty-state-description">
                  {debouncedValue
                    ? t("NO_SEARCH_RESULTS_DESCRIPTION")
                    : t("NO_USERS_DESCRIPTION")}
                </p>
                {!debouncedValue && (
                  <Link
                    to="create"
                    className="btn btn-primary d-inline-flex align-items-center gap-2"
                    style={{ gap: "0.5rem", fontWeight: "500" }}
                  >
                    <UserAdd02Icon
                      style={{ width: "18px", height: "18px" }}
                    />
                    <span>{t("CREATE_FIRST_USER")}</span>
                  </Link>
                )}
              </div>
            ) : (
              users.map((user) => <UserCard key={user?.id} userData={user} />)
            )}
          </Stack>

          {usersRes?.data?.meta?.per_page && !loading && (
            <AntPagination
              className="sticky-bottom z-1 w-100 p-3 bg-white"
              hideOnSinglePage
              pageSize={usersRes?.data?.meta?.per_page?.[0]}
              current={currentPage}
              onChange={handlePageChange}
              total={usersRes?.data?.meta?.total?.[0]}
            />
          )}
        </Stack>

      </Container>
    </div>
  );
};

export default Users;
